import { Component, inject, signal, computed, OnInit, OnDestroy, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { Menu, MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

import { PaymentService, MemberPayment } from '../../../services/payment';
import { ReceiptDownloader } from '../../../services/receipt-downloader';
import { CoiDownloader } from '../../../services/coi-downloader';
import { PageTitleService } from '../../../services/page-title';
import { IndDatePipe } from '../../../pipes/ind-date';
import { ReceiptView } from '../receipt/receipt-view';
import { CoiView } from '../../policies/coi-view/coi-view';
import { SendCoiDialog } from '../../policies/send-coi/send-coi-dialog';

@Component({
  selector: 'app-payments-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    MenuModule,
    TagModule,
    TooltipModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    IndDatePipe,
    ReceiptView,
    CoiView,
    SendCoiDialog,
  ],
  templateUrl: './payments-list.html',
  styleUrls: ['./payments-list.css'],
})
export class PaymentsListPage implements OnInit, OnDestroy {
  private svc = inject(PaymentService);
  private receipts = inject(ReceiptDownloader);
  private cois = inject(CoiDownloader);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);
  private qpSub?: Subscription;

  loading = signal(false);
  search = signal('');
  rows = signal<MemberPayment[]>([]);

  // view dialog (read-only)
  showView = signal(false);
  viewing = signal<MemberPayment | null>(null);

  // receipt dialog (shared by every SUCCESS row's "View receipt")
  showReceipt = signal(false);
  receiptPaymentId = signal<number | null>(null);

  // certificate dialog (shared by every SUCCESS row's "View certificate", keyed by member id)
  showCoi = signal(false);
  coiMemberId = signal<number | null>(null);

  // "Send Certificate" dialog — queues the COI over WhatsApp / SMS / Email.
  showSendCoi = signal(false);
  sendCoiMemberId = signal<number | null>(null);

  filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.rows();
    return this.rows().filter(
      (r) =>
        (r.memberName || '').toLowerCase().includes(q) ||
        (r.memberUniqueId || '').toLowerCase().includes(q) ||
        (r.paymentUniqueId || '').toLowerCase().includes(q) ||
        (r.paymentStatus || '').toLowerCase().includes(q) ||
        (r.gatewayName || '').toLowerCase().includes(q),
    );
  });

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    this.load();
    // Deep link (global search): /payments/list?q=<payment uid | member | status> —
    // the grid filters client-side, so just seed the search box.
    this.qpSub = this.route.queryParamMap.subscribe((pm) => {
      const q = (pm.get('q') || '').trim();
      if (q) this.search.set(q);
    });
  }

  ngOnDestroy() {
    this.qpSub?.unsubscribe();
  }

  load() {
    this.loading.set(true);
    this.svc.payGetAll().subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  // ===================== display helpers =====================
  payerLabel(r: MemberPayment): string {
    if (r.payerType === 'PARTNER') return `PARTNER${r.partnerId ? ' #' + r.partnerId : ''}`;
    return `CLIENT${r.clientRef ? ' · ' + r.clientRef : ''}`;
  }

  statusSeverity(s?: string | null): 'success' | 'danger' | 'warn' | 'info' | 'secondary' {
    switch ((s || '').toUpperCase()) {
      case 'SUCCESS':
        return 'success';
      case 'FAILED':
      case 'CANCELLED':
        return 'danger';
      case 'REFUNDED':
        return 'info';
      case 'PENDING':
      case 'INITIATED':
        return 'warn';
      default:
        return 'secondary';
    }
  }

  // ===================== receipt (SUCCESS rows only) =====================
  /** Receipts are generated only for successful payments — the backend 400s otherwise. */
  hasReceipt(r: MemberPayment): boolean {
    return (r.paymentStatus || '').toUpperCase() === 'SUCCESS';
  }

  viewReceipt(r: MemberPayment) {
    this.receiptPaymentId.set(r.id);
    this.showReceipt.set(true);
  }

  printReceipt(r: MemberPayment) {
    this.receipts.printHtml(r.id);
  }

  downloadReceipt(r: MemberPayment) {
    this.receipts.downloadPdf(r.id, r.paymentUniqueId);
  }

  // ===================== certificate (issued = SUCCESS payment) =====================
  viewCoi(r: MemberPayment) {
    this.coiMemberId.set(r.memberId);
    this.showCoi.set(true);
  }

  /** Open the "Send Certificate" dialog — queues the COI over WhatsApp / SMS / Email. */
  sendCoi(r: MemberPayment) {
    if (!r?.memberId) return;
    this.sendCoiMemberId.set(r.memberId);
    this.showSendCoi.set(true);
  }

  printCoi(r: MemberPayment) {
    this.cois.printCoi(r.memberId);
  }

  downloadCoi(r: MemberPayment) {
    this.cois.downloadCoi(r.memberId);
  }

  // ===================== row overflow menu =====================
  /** One popup menu shared by all rows — rebuilt for the clicked row, then toggled. */
  rowMenuItems: MenuItem[] = [];

  openRowMenu(menu: Menu, event: Event, r: MemberPayment) {
    this.rowMenuItems = [
      { label: 'Print receipt', icon: 'pi pi-print', command: () => this.printReceipt(r) },
      { label: 'Download receipt PDF', icon: 'pi pi-download', command: () => this.downloadReceipt(r) },
      { separator: true },
      { label: 'View certificate', icon: 'pi pi-id-card', command: () => this.viewCoi(r) },
    ];
    menu.toggle(event);
  }

  // ===================== details page =====================
  /** Open the full Payment Details page for this row (any status). */
  openDetail(r: MemberPayment) {
    this.router.navigate(['/payments/detail', r.id]);
  }

  // ===================== view (read-only quick dialog) =====================
  openView(r: MemberPayment) {
    this.viewing.set(r);
    this.showView.set(true);
  }

  closeView() {
    this.showView.set(false);
  }

  prettyJson(s?: string | null): string {
    if (!s) return '';
    try {
      return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
      return s;
    }
  }
}
